import { createColumns } from './projects-tables/columns';
import { DataTable } from '@/components/ui/table/data-table';
import { useGetProjects } from '@/services/projects/queries';
import { DataTableSkeleton } from '@/components/ui/table/data-table-skeleton';
import ProjectTableAction from './projects-tables/project-table-action';
import { Icons } from '@/components/icons';
import { useState, useMemo } from 'react';
import { useProjectTableFilters } from './projects-tables/use-case-table-filters';
import type { ProjectListItem } from '@/services/types';
import { useAuthStore } from '@/store/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ColumnDef } from '@tanstack/react-table';

export default function ProjectsTable() {
  const { searchQuery, sort, organisation, setOrganisation } = useProjectTableFilters();
  
  const [activeTab, setActiveTab] = useState<'all' | 'my'>('all');

  const { user } = useAuthStore();

  const activeOrganisation = user?.organisation?.id;

  // Create columns with activeOrganisation for badge and dropdown logic
  const columns = useMemo(() => createColumns(activeOrganisation), [activeOrganisation]);

  // Handle tab change and update organisation filter
  const handleTabChange = (value: string) => {
    setActiveTab(value as 'all' | 'my');
    if (value === 'my' && activeOrganisation) {
      setOrganisation(activeOrganisation);
    } else {
      setOrganisation(null);
    }
  };

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo(() => ({
    page: 1, // Default page
    limit: 20, // Default limit
    ...(searchQuery && { search: searchQuery }),
    ...(sort && { sort }),
    ...(organisation && { organisation })
  }), [searchQuery, sort, organisation]);

  const { data, isLoading } = useGetProjects(filters, {
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  // Memoize data arrays to prevent unnecessary re-renders
  const tableData: ProjectListItem[] = useMemo(() => data?.data || [], [data?.data]);
  const total = useMemo(() => data?.pagination?.totalDocs ?? 0, [data?.pagination?.totalDocs]);

  if (isLoading) return <DataTableSkeleton />;

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className='h-full space-y-4'>
      <TabsList className='w-full border-b px-5'>
        <TabsTrigger value='all'>
          All Projects {activeTab === 'all' && `(${total})`}
        </TabsTrigger>
        {activeOrganisation && (
          <TabsTrigger value='my'>
            My Projects {activeTab === 'my' && `(${total})`}
          </TabsTrigger>
        )}
      </TabsList>

      <TableTabContent value='all' columns={columns} tableData={tableData} total={total} />

      <TableTabContent value='my' columns={columns} tableData={tableData} total={total} />
    </Tabs>
  );
}

type TableTabContentProps = {
  value: string;
  columns: ColumnDef<ProjectListItem>[];
  tableData: ProjectListItem[];
  total: number;
};

function TableTabContent({ value, columns, tableData, total }: TableTabContentProps) {
  return (
    <TabsContent value={value} className='h-full space-y-4'>
      <ProjectTableAction data={tableData} />
      <div className='px-4'>
        <DataTable
          columns={columns}
          data={tableData}
          totalItems={total}
          emptyState={
            <div className='flex h-full w-full flex-col items-center justify-center'>
              <Icons.emptyState />
              No projects found
            </div>
          }
        />
      </div>
    </TabsContent>
  );
}